import { useState, useEffect } from 'react';

function getSecondsUntil(target: Date): number {
  const now = Date.now();
  const targetTime = target.getTime();
  return Math.max(0, Math.floor((targetTime - now) / 1000));
}

export interface CountdownParts {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

function secondsToParts(seconds: number): CountdownParts {
  if (seconds <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h >= 24) {
    return {
      days: Math.floor(h / 24),
      hours: h % 24,
      minutes: m,
      seconds: s,
    };
  }
  return { days: 0, hours: h, minutes: m, seconds: s };
}

export function useCountdownToDate(
  targetDate: string | Date | null
): CountdownParts {
  const targetStr =
    targetDate instanceof Date ? targetDate.toISOString() : targetDate;
  const target = targetStr ? new Date(targetStr) : null;
  const [secondsLeft, setSecondsLeft] = useState(() =>
    target && !Number.isNaN(target.getTime()) ? getSecondsUntil(target) : 0
  );

  useEffect(() => {
    if (!targetStr) return;
    const target = new Date(targetStr);
    if (Number.isNaN(target.getTime())) return;
    setSecondsLeft(getSecondsUntil(target));

    const interval = setInterval(() => {
      const next = getSecondsUntil(target);
      setSecondsLeft(next);
      if (next <= 0) clearInterval(interval);
    }, 1000);

    return () => clearInterval(interval);
  }, [targetStr]);

  return secondsToParts(secondsLeft);
}
